<div class="chatbot-container">

    <!-- Sidebar: Sessions -->
    <div class="sidebar" [class.sidebar-open]="sidebarVisible" [class.sidebar-collapsed]="!sidebarVisible">
      <button class="new-chat-btn" (click)="newChat()" [disabled]="isStreaming">
        <i class="pi pi-plus"></i>
        {{ 'chatbot.newChat' | transloco }}
      </button>

      <h4>{{ 'chatbot.sessions' | transloco }}</h4>

      <div *ngIf="sessionsLoading && sessions.length === 0" class="sidebar-loading">
        <p-progressSpinner styleClass="small-spinner" />
      </div>

      <div *ngIf="!sessionsLoading && sessions.length === 0" class="sidebar-empty">
        <p>{{ 'chatbot.noSessions' | transloco }}</p>
      </div>

      <ul>
        <li *ngFor="let session of sessions; trackBy: trackBySessionId"
            [class.active]="currentSessionId === session.session_id"
            (click)="selectSession(session)">

          <!-- Normal display -->
          <ng-container *ngIf="renamingSessionId !== session.session_id">
            <div class="session-info">
              <span class="session-title">{{ session.title }}</span>
              <span class="session-preview">{{ session.last_message_preview }}</span>
            </div>
            <div class="session-actions">
              <button class="session-action-btn" (click)="startRename(session, $event)" pTooltip="{{ 'chatbot.renameTitle' | transloco }}" aria-label="Renomear sessão">
                <i class="pi pi-pencil"></i>
              </button>
              <button class="session-action-btn session-delete-btn" (click)="deleteSession(session, $event)" aria-label="Excluir sessão">
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </ng-container>

          <!-- Rename mode -->
          <ng-container *ngIf="renamingSessionId === session.session_id">
            <input type="text"
                   class="rename-input"
                   [(ngModel)]="renameValue"
                   maxlength="100"
                   (keydown.enter)="confirmRename(session)"
                   (keydown.escape)="cancelRename()"
                   (click)="$event.stopPropagation()" />
            <div class="session-actions">
              <button class="session-action-btn" (click)="confirmRename(session); $event.stopPropagation()">
                <i class="pi pi-check"></i>
              </button>
              <button class="session-action-btn" (click)="cancelRename(); $event.stopPropagation()">
                <i class="pi pi-times"></i>
              </button>
            </div>
          </ng-container>
        </li>
      </ul>

      <button *ngIf="nextCursor" class="load-more-btn" (click)="loadMoreSessions()" [disabled]="sessionsLoading">
        <i class="pi pi-angle-down"></i>
        {{ 'chatbot.loadMore' | transloco }}
      </button>
    </div>

    <!-- Chat Area -->
    <div class="chat-area">
      <div class="chat-header">
        <button class="sidebar-toggle-btn" [class.active]="sidebarVisible" (click)="toggleSidebar()"
                [attr.aria-label]="sidebarVisible ? 'Fechar menu' : 'Abrir menu'">
          <i [class]="sidebarVisible ? 'pi pi-chevron-left' : 'pi pi-bars'"></i>
        </button>
        <h3>{{ 'chatbot.header' | transloco }}</h3>
        <div class="chat-header-actions">
          <span *ngIf="currentProfileName" class="model-badge" [class.model-pro]="displayedProfileKey === 'max-v01'">
            {{ currentProfileName }}
          </span>
          <button *ngIf="isAdminUser && currentSessionId"
                  class="chat-export-btn"
                  [disabled]="isExportingConversation"
                  (click)="downloadConversationJson()"
                  pTooltip="{{ 'chatbot.exportTooltip' | transloco }}"
                  aria-label="Baixar conversa em JSON">
            <i class="pi" [ngClass]="isExportingConversation ? 'pi-spin pi-spinner' : 'pi-download'"></i>
          </button>
        </div>
      </div>

      <div class="chat-scroll" #chatScroll (scroll)="onChatScroll($event)">
        <div class="messages-inner">

        <!-- Messages loading -->
        <div *ngIf="messagesLoading" class="chat-loading">
          <p-progressSpinner styleClass="small-spinner" />
        </div>

        <!-- Empty state -->
        <div *ngIf="!messagesLoading && messages.length === 0 && !isStreaming" class="chat-empty">
          <div class="chat-empty__mark">
            <ng-container *ngTemplateOutlet="turboMark"></ng-container>
          </div>
          <h2 class="chat-empty__title">{{ 'chatbot.empty.title' | transloco }}</h2>
          <p class="chat-empty__lead">{{ 'chatbot.empty.lead' | transloco }}</p>

          <div class="chat-empty__starters" *ngIf="emptyStarters.length">
            <span class="chat-empty__eyebrow">{{ 'chatbot.empty.startersLabel' | transloco }}</span>
            <button *ngFor="let s of emptyStarters; trackBy: trackByStarterId"
                    type="button"
                    class="starter"
                    [class.starter--locked]="s.locked"
                    [disabled]="s.locked"
                    (click)="runStarter(s)">
              <i class="pi starter__icon" [ngClass]="'pi-' + s.icon"></i>
              <span class="starter__label">{{ s.labelKey ? (s.labelKey | transloco) : s.label }}</span>
              <span *ngIf="s.locked" class="paid-tag">{{ 'chatbot.paidPlanTag' | transloco }}</span>
              <i *ngIf="!s.locked" class="pi pi-angle-right starter__go"></i>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div *ngFor="let msg of messages; trackBy: trackByMessageId" [ngClass]="msg.role" class="chat-message">
          <!-- Aviso inline de troca de perfil -->
          <div *ngIf="msg.event_type === 'profile_change'" class="model-change-notice">
            <i class="pi pi-sync"></i>{{ msg.content }}
          </div>
          <div *ngIf="msg.event_type !== 'profile_change'" class="bubble" [class.guardrail-blocked]="msg.is_guardrail_blocked" [class.system-msg]="msg.is_system || isSkillMessage(msg.content)">
            <!-- Skill message -->
            <ng-container *ngIf="isSkillMessage(msg.content); else normalBubble">
              <div class="skill-bubble-content">
                <i class="pi skill-bubble-icon" [ngClass]="'pi-' + getSkillIcon(msg.content)"></i>
                <span>{{ getSkillDisplayName(msg.content) }}</span>
              </div>
            </ng-container>
            <!-- Normal message -->
            <ng-template #normalBubble>
              <div *ngIf="msg.reasoning_content" class="thinking-section">
                <button class="thinking-toggle" (click)="toggleThinking(msg.message_id)">
                  <i class="pi pi-bolt"></i>
                  {{ 'chatbot.thinking' | transloco }}
                  <i [class]="expandedThinkingIds.has(msg.message_id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
                </button>
                <div *ngIf="expandedThinkingIds.has(msg.message_id)" class="thinking-content">{{ msg.reasoning_content }}</div>
              </div>
              <!-- Trace (debug) events -->
              <div *ngIf="isTraceAdmin && traceModeActive && msg.traceEvents?.length" class="trace-section">
                <div *ngFor="let ev of msg.traceEvents; let i = index; trackBy: trackByIndex" class="trace-event" [ngClass]="'trace-type-' + ev.type">
                  <button class="trace-toggle" (click)="toggleTraceEvent(msg.message_id + '-' + i)">
                    <i class="pi trace-icon" [ngClass]="getTraceIcon(ev.type)"></i>
                    <span class="trace-summary">{{ getTraceSummary(ev) }}</span>
                    <i class="pi trace-chevron" [ngClass]="isTraceEventExpanded(msg.message_id + '-' + i) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
                  </button>
                  <pre *ngIf="isTraceEventExpanded(msg.message_id + '-' + i)" class="trace-json">{{ ev | json }}</pre>
                </div>
              </div>
              <!-- Progress (narração pública): pílula "Concluído", clicável p/ reexpandir -->
              <div *ngIf="msg.progressSteps?.length" class="progress-section">
                <button class="progress-pill" (click)="toggleProgress(msg.message_id)">
                  <i class="pi pi-check-circle"></i>
                  <span>{{ 'chatbot.progressDone' | transloco }}</span>
                  <i class="pi" [ngClass]="isProgressExpanded(msg.message_id) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
                </button>
                <div class="progress-list" *ngIf="isProgressExpanded(msg.message_id)">
                  <div *ngFor="let s of msg.progressSteps; trackBy: trackByIndex" class="progress-step">
                    <i class="pi pi-check"></i>
                    <span class="progress-label">{{ s.label }}{{ (s.count ?? 1) > 1 ? ' (' + s.count + ')' : '' }}</span>
                    <span class="progress-detail" *ngIf="s.detail">· {{ s.detail }}</span>
                  </div>
                </div>
              </div>
              <!-- Artifacts acima do texto, na mesma posição da bolha de streaming, para não trocarem de lugar no done. -->
              <app-datalog-chart *ngIf="msg.datalogChart" [data]="msg.datalogChart"></app-datalog-chart>
              <app-map-artifact *ngFor="let m of msg.mapArtifacts" [map]="m"></app-map-artifact>
              <app-map-artifact *ngFor="let c of msg.correctionArtifacts" [correction]="c"></app-map-artifact>
              <!-- Uma corrida por artifact: o contrato permite mais de um no mesmo turno. -->
              <app-drag-run-card *ngFor="let run of msg.dragRuns" [data]="run"></app-drag-run-card>
              <!-- Um gráfico por artifact; o contrato aceita vários no mesmo turno. -->
              <app-chart-artifact *ngFor="let c of msg.charts" [payload]="c"></app-chart-artifact>
              <div class="bubble-content markdown-body" [innerHTML]="renderContent(getDisplayContent(msg.content, msg.role), msg.content_format)"></div>
              <span *ngIf="msg.upgradeAction" class="upgrade-link" (click)="openPlansModal()">
                {{ 'chatbot.upgradeLink' | transloco }}
              </span>
              <span *ngIf="msg.topUpAction" class="upgrade-link" (click)="navigateToTopUp()">
                {{ 'chatbot.topUpLink' | transloco }}
              </span>
              <!-- FTCF Canvas -->
              <app-ftcf-canvas
                *ngIf="msg.ftcfArtifact"
                [title]="msg.ftcfArtifact.title"
                [nodes]="msg.ftcfArtifact.nodes"
                [links]="msg.ftcfArtifact.links">
              </app-ftcf-canvas>
              <!-- FTCF Download -->
              <div *ngIf="msg.ftcfFile" class="ftcf-download">
                <button class="ftcf-download-btn" (click)="downloadFtcf(msg.ftcfFile!)">
                  <i class="pi pi-download"></i>
                  {{ msg.ftcfFile.title }}.ftcf
                </button>
              </div>
              <!-- Clarification history (answered or pending from history) -->
              <div *ngIf="msg.clarification_question !== undefined" class="clarification-history-bubble">
                <p class="clarification-history-question">{{ msg.clarification_question }}</p>
                <div *ngIf="msg.status === 'answered' && msg.user_response?.text" class="clarification-history-answer">
                  <span class="option-check">✓</span>
                  <span>{{ msg.user_response!.text }}</span>
                </div>
                <span *ngIf="msg.status !== 'answered'" class="clarification-history-pending">Aguardando resposta...</span>
              </div>
            </ng-template>
          </div>

          <!-- Sent attachment chips (user messages only) -->
          <div *ngIf="msg.role === 'user' && msg.attachments?.length" class="pending-attachments-row">
            <span *ngFor="let att of msg.attachments; trackBy: trackByAttachmentId" class="pending-attachment-chip">
              <i class="pi" [class.pi-map]="att.type === 'map'" [class.pi-file]="att.type === 'log'"></i>
              <span class="pending-attachment-name">{{ att.fileName }}</span>
            </span>
          </div>

          <!-- Feedback buttons (assistant only) -->
          <div *ngIf="msg.role === 'assistant' && msg.message_id && !msg.message_id.startsWith('temp-')" class="feedback-actions">
            <button class="feedback-btn"
                    [class.feedback-active]="msg.feedback_rating === 1"
                    [disabled]="feedbackLoadingId === msg.message_id"
                    aria-label="Marcar resposta como útil"
                    (click)="onFeedback(msg, 1)">
              <i [class]="feedbackLoadingId === msg.message_id ? 'pi pi-spin pi-spinner' : 'pi pi-thumbs-up'"></i>
            </button>
            <button class="feedback-btn"
                    [class.feedback-active]="msg.feedback_rating === -1"
                    [disabled]="feedbackLoadingId === msg.message_id"
                    aria-label="Marcar resposta como não útil"
                    (click)="onFeedback(msg, -1)">
              <i class="pi pi-thumbs-down"></i>
            </button>
          </div>

          <!-- Dislike form -->
          <div *ngIf="dislikeMessageId === msg.message_id" class="dislike-form">
            <p class="dislike-title">{{ 'chatbot.dislikeTitle' | transloco }}</p>
            <div class="dislike-reasons">
              <button *ngFor="let reason of dislikeReasonOptions; trackBy: trackByIndex"
                      class="dislike-reason-chip"
                      [class.selected]="dislikeReasons.includes(reason.id)"
                      (click)="toggleDislikeReason(reason.id)">
                {{ reason.label }}
              </button>
            </div>
            <textarea class="dislike-comment"
                      [(ngModel)]="dislikeComment"
                      maxlength="1000"
                      [placeholder]="'chatbot.dislikeCommentPlaceholder' | transloco"
                      rows="2">
            </textarea>
            <div class="dislike-form-actions">
              <button class="dislike-cancel-btn" (click)="cancelDislike()">
                {{ 'chatbot.dislikeCancel' | transloco }}
              </button>
              <button class="dislike-submit-btn" (click)="submitDislike()" [disabled]="dislikeSending">
                <i *ngIf="dislikeSending" class="pi pi-spin pi-spinner"></i>
                {{ 'chatbot.dislikeSubmit' | transloco }}
              </button>
            </div>
          </div>
        </div>

        <!-- Streaming bubble -->
        <div *ngIf="isStreaming" class="chat-message assistant">
          <!-- Tool call status chips -->
          <div *ngIf="activeToolCalls.length > 0" class="streaming-status">
            <span *ngFor="let tc of activeToolCalls; trackBy: trackByToolCallId" class="tool-call-chip">
              <i class="pi pi-wrench"></i> {{ tc.tool_name }}
            </span>
          </div>
          <!-- Agent handoff indicator -->
          <div *ngIf="streamingAgentHandoff" class="streaming-status">
            <span class="agent-handoff-chip">
              <i class="pi pi-arrow-right-arrow-left"></i>
              {{ streamingAgentHandoff.from }} → {{ streamingAgentHandoff.to }}
            </span>
          </div>
          <!-- Trace (debug) events -->
          <div *ngIf="isTraceAdmin && traceModeActive && streamingTraceEvents.length > 0" class="trace-section">
            <div *ngFor="let ev of streamingTraceEvents; let i = index; trackBy: trackByIndex" class="trace-event" [ngClass]="'trace-type-' + ev.type">
              <button class="trace-toggle" (click)="toggleTraceEvent('streaming-' + i)">
                <i class="pi trace-icon" [ngClass]="getTraceIcon(ev.type)"></i>
                <span class="trace-summary">{{ getTraceSummary(ev) }}</span>
                <i class="pi trace-chevron" [ngClass]="isTraceEventExpanded('streaming-' + i) ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
              </button>
              <pre *ngIf="isTraceEventExpanded('streaming-' + i)" class="trace-json">{{ ev | json }}</pre>
            </div>
          </div>
          <!-- Progress (narração pública): lista de etapas → pílula ao concluir -->
          <div *ngIf="streamingProgressSteps.length" class="progress-section">
            <div class="progress-list">
              <div *ngFor="let s of streamingProgressSteps; trackBy: trackByIndex" class="progress-step">
                <i class="pi" [ngClass]="s.status === 'done' ? 'pi-check' : 'pi-spin pi-spinner'"></i>
                <span class="progress-label">{{ s.label }}{{ (s.count ?? 1) > 1 ? ' (' + s.count + ')' : '' }}</span>
                <span class="progress-detail" *ngIf="s.detail">· {{ s.detail }}</span>
              </div>
            </div>
          </div>
          <!-- Artifacts chegam no meio do turno (antes do replace que limpa o preâmbulo): já aparecem aqui. -->
          <app-datalog-chart *ngIf="streamingDatalogChart" [data]="streamingDatalogChart"></app-datalog-chart>
          <app-map-artifact *ngFor="let m of streamingMapArtifacts" [map]="m"></app-map-artifact>
          <app-map-artifact *ngFor="let c of streamingCorrectionArtifacts" [correction]="c"></app-map-artifact>
          <app-drag-run-card *ngFor="let run of streamingDragRuns" [data]="run"></app-drag-run-card>
          <app-chart-artifact *ngFor="let c of streamingCharts" [payload]="c"></app-chart-artifact>
          <div class="bubble streaming-bubble">
            <div class="bubble-content markdown-body" *ngIf="streamingContent" [innerHTML]="renderContent(streamingContent, 'markdown', false)"></div>
            <div class="streaming-indicator" *ngIf="!streamingContent">
              <ng-container *ngTemplateOutlet="turboMark"></ng-container>
            </div>
          </div>
        </div>

        </div><!-- /messages-inner -->
      </div>

      <!-- Scroll to bottom button -->
      <button *ngIf="userScrolledUp && isStreaming"
              class="scroll-to-bottom-btn"
              (click)="scrollToBottomForce()"
              aria-label="Ir ao final da conversa">
        <i class="pi pi-angle-down"></i>
      </button>

      <!-- Retry button (service at capacity) -->
      <div *ngIf="canRetry" class="retry-container">
        <button pButton
                class="p-button-outlined p-button-secondary retry-btn"
                icon="pi pi-refresh"
                [label]="'chatbot.retry' | transloco"
                (click)="retryLastMessage()">
        </button>
      </div>

      <!-- Clarify ancorado acima do dock, fora do scroll de mensagens: dentro
           dele a caixa era cortada pela borda da area rolavel. Alinha com a
           largura do campo de digitar, que e onde se responde "Outro". -->
      <div class="clarify-dock" *ngIf="clarificationData">
        <div class="messages-inner">
          <div class="clarify">
            <button type="button"
                    class="clarify__dismiss"
                    (click)="dismissClarification()"
                    [attr.aria-label]="'chatbot.clarify.dismiss' | transloco"
                    [pTooltip]="'chatbot.clarify.dismiss' | transloco">
              <i class="pi pi-times"></i>
            </button>
            <p class="clarify__intro" *ngIf="clarificationData.intro">{{ clarificationData.intro }}</p>

            <div class="clarify__tabs" *ngIf="clarificationData.questions.length > 1">
              <button *ngFor="let q of clarificationData.questions; let i = index"
                      type="button"
                      #clarifyTabEl
                      class="clarify__tab"
                      [class.clarify__tab--active]="i === currentClarificationTab"
                      [class.clarify__tab--done]="isClarificationQuestionAnswered(q)"
                      [attr.aria-selected]="i === currentClarificationTab"
                      (click)="setClarificationTab(i)">
                <i class="pi pi-check clarify__tab-check"></i>
                <span class="clarify__tab-label">{{ q.question }}</span>
              </button>
            </div>

            <ng-container *ngFor="let q of clarificationData.questions; let i = index">
              <div class="clarify__panel" *ngIf="i === currentClarificationTab">
                <p class="clarify__question">{{ q.question }}</p>

                <!-- Rola a partir da 3a opcao; o botao de enviar fica fora
                     deste container, entao nunca sai da vista. -->
                <div class="clarify__options" *ngIf="!isFreeTextQuestion(q)" role="listbox" [attr.aria-multiselectable]="!!q.multiSelect">
                  <button *ngFor="let opt of q.options; trackBy: trackByIndex"
                          type="button"
                          role="option"
                          class="clarify__option"
                          [class.clarify__option--selected]="isOptionSelected(q.id, opt.label)"
                          [attr.aria-selected]="isOptionSelected(q.id, opt.label)"
                          (click)="pickClarificationOption(q, opt.label)">
                    <span class="clarify__mark" [class.clarify__mark--box]="q.multiSelect"></span>
                    <span class="clarify__body">
                      <span class="clarify__label">
                        {{ opt.label }}
                        <span class="clarify__badge" *ngIf="opt.recommended">{{ 'chatbot.clarify.recommended' | transloco }}</span>
                      </span>
                      <span class="clarify__desc" *ngIf="opt.description">{{ opt.description }}</span>
                    </span>
                  </button>

                  <button *ngIf="q.allowOther"
                          type="button"
                          role="option"
                          class="clarify__option"
                          [class.clarify__option--selected]="isOtherSelected(q.id)"
                          [attr.aria-selected]="isOtherSelected(q.id)"
                          (click)="toggleClarificationOther(q.id, q.multiSelect)">
                    <span class="clarify__mark" [class.clarify__mark--box]="q.multiSelect"></span>
                    <span class="clarify__body">
                      <span class="clarify__label">{{ 'chatbot.clarify.other' | transloco }}</span>
                    </span>
                  </button>
                </div>

                <input *ngIf="isFreeTextQuestion(q) || isOtherSelected(q.id)"
                       #clarifyOtherEl
                       type="text"
                       class="clarify__other"
                       maxlength="300"
                       [ngModel]="clarificationOtherMap[q.id] || ''"
                       (ngModelChange)="clarificationOtherMap[q.id] = $event"
                       [placeholder]="'chatbot.clarify.otherPlaceholder' | transloco"
                       (keydown.enter)="canClarifyPrimary && onClarifyPrimary()" />
              </div>
            </ng-container>

            <div class="clarify__footer">
              <span class="clarify__status" *ngIf="clarificationData.questions.length > 1">
                {{ 'chatbot.clarify.progress' | transloco: { done: answeredClarificationCount(), total: clarificationData.questions.length } }}
              </span>
              <button type="button"
                      class="clarify__submit"
                      [disabled]="!canClarifyPrimary"
                      (click)="onClarifyPrimary()">
                <i class="pi" [ngClass]="clarifyPrimaryIsSubmit ? 'pi-send' : 'pi-arrow-right'"></i>
                <span>{{ (clarifyPrimaryIsSubmit ? 'chatbot.clarify.submit' : 'chatbot.clarify.next') | transloco }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Input -->
      <div class="chat-input">
        <div class="messages-inner">
          <div class="chat-input-card">
            <!-- @debug popup (admin only) -->
            <div *ngIf="atDebugMention" class="at-file-popup">
              <div class="at-file-item at-file-active" (mousedown)="selectDebugMode()">
                <i class="pi pi-bug at-file-icon"></i>
                <div class="at-file-info">
                  <span class="at-file-path">{{ (traceModeActive ? 'chatbot.atDebug.deactivate' : 'chatbot.atDebug.activate') | transloco }}</span>
                </div>
                <span class="at-file-badge">{{ 'chatbot.atDebug.badge' | transloco }}</span>
              </div>
            </div>

            <!-- @file popup -->
            <!-- @spec popup -->
            <div *ngIf="atSpecMention" class="at-file-popup">
              <div *ngIf="!atSpecResults.length" class="at-file-status">
                {{ 'chatbot.atSpec.noResults' | transloco }}
              </div>
              <div *ngFor="let s of atSpecResults; let i = index; trackBy: trackByIndex"
                   class="at-file-item"
                   [class.at-file-active]="i === atSpecSelectedIndex"
                   (mousedown)="selectAtSpec(s)"
                   (mouseenter)="atSpecSelectedIndex = i">
                <i class="pi at-file-icon" [ngClass]="'pi-' + s.icon"></i>
                <div class="at-file-info">
                  <span class="at-file-path">{{ s.name }}</span>
                </div>
                <span class="at-file-badge">{{ 'chatbot.atSpec.badge' | transloco }}</span>
              </div>
            </div>

            <!-- @file popup -->
            <div *ngIf="atFileMention" class="at-file-popup">
              <div *ngIf="existingFilesLoading" class="at-file-status">
                <i class="pi pi-spin pi-spinner"></i> {{ 'chatbot.atFile.loading' | transloco }}
              </div>
              <div *ngIf="!existingFilesLoading && !atFileResults.length" class="at-file-status">
                {{ 'chatbot.atFile.noResults' | transloco }}
              </div>
              <div *ngFor="let f of atFileResults; let i = index; trackBy: trackByIndex"
                   class="at-file-item"
                   [class.at-file-active]="i === atFileSelectedIndex"
                   (mousedown)="selectAtFile(f)"
                   (mouseenter)="atFileSelectedIndex = i">
                <i class="pi at-file-icon"
                   [class.pi-map]="(f.type || '') === 'map'"
                   [class.pi-file]="(f.type || '') !== 'map'"></i>
                <div class="at-file-info">
                  <span class="at-file-path">
                    <span *ngIf="extractBrowseFolder(f.pathFile)" class="at-file-folder">{{ extractBrowseFolder(f.pathFile) }}/</span>{{ f.fileName }}
                  </span>
                </div>
                <span class="at-file-badge">{{ (f.type || '') === 'map' ? ('chatbot.atFile.badgeMap' | transloco) : ('chatbot.atFile.badgeLog' | transloco) }}</span>
              </div>
            </div>

            <!-- Pending attachment chips -->
            <div *ngIf="pendingAttachments.length" class="pending-attachments-row">
              <span *ngFor="let att of pendingAttachments; trackBy: trackByAttachmentId"
                    class="pending-attachment-chip"
                    [class.pending-attachment-chip--error]="att.status === 'error'">
                <i class="pi" [class.pi-map]="att.type === 'map'" [class.pi-file]="att.type === 'log'"></i>
                <span class="pending-attachment-name">{{ att.fileName }}</span>
                <i *ngIf="att.status === 'uploading'" class="pi pi-spin pi-spinner pending-attachment-spinner"></i>
                <span *ngIf="att.status === 'uploading'" class="pending-attachment-progress">{{ att.progress ?? 0 }}%</span>
                <button *ngIf="att.status !== 'uploading'"
                        type="button"
                        class="pending-attachment-remove"
                        (click)="removePendingAttachment(att.id)"
                        [attr.aria-label]="'chatbot.attachments.remove' | transloco">
                  <i class="pi pi-times"></i>
                </button>
              </span>
            </div>

            <textarea
              pInputTextarea
              [rows]="1"
              [autoResize]="true"
              #chatInputEl
              [style.max-height.px]="inputMaxHeightPx"
              class="chat-input-field"
              [(ngModel)]="userInput"
              [placeholder]="'chatbot.placeholder' | transloco"
              (keydown)="onInputKeydown($event)"
              (input)="onUserInputChange()"
              (blur)="onInputBlur()"
              [disabled]="isStreaming || rateLimitCountdown > 0 || !!clarificationData || !profilesEnabled"
            ></textarea>
            <div class="chat-input-footer">
              <button *ngIf="isStreaming"
                      class="stop-btn"
                      (click)="stopStreaming()"
                      aria-label="Parar geração de resposta">
                <i class="pi pi-stop-circle"></i>
              </button>
              <!-- Attach menu -->
              <div class="attach-wrapper">
                <div *ngIf="attachMenuOpen" class="dock-menu__backdrop" (click)="closeAttachMenu()"></div>

                <!-- Painel unico que troca de conteudo ao entrar num nivel. Era
                     um flyout lateral: cada nivel novo crescia para o lado e
                     saia da tela. Aqui a largura nunca muda. -->
                <div *ngIf="attachMenuOpen" class="dock-menu dock-menu--left">

                  <ng-container *ngIf="attachMenuView === 'main'">
                    <button type="button" class="dock-menu__item"
                            [class.dock-menu__item--locked]="attachLocked"
                            [disabled]="attachLocked"
                            (click)="closeAttachMenu(); openChatUploadDialog()">
                      <i class="pi pi-paperclip dock-menu__icon"></i>
                      <span class="dock-menu__name">{{ 'chatbot.attachMenu.sendFiles' | transloco }}</span>
                      <span *ngIf="attachLocked" class="paid-tag">{{ 'chatbot.paidPlanTag' | transloco }}</span>
                    </button>
                    <button type="button" class="dock-menu__item"
                            (click)="attachMenuView = 'skills'">
                      <i class="pi pi-bolt dock-menu__icon"></i>
                      <span class="dock-menu__name">{{ 'chatbot.attachMenu.selectExpert' | transloco }}</span>
                      <i class="pi pi-chevron-right dock-menu__go"></i>
                    </button>
                  </ng-container>

                  <ng-container *ngIf="attachMenuView === 'skills'">
                    <button type="button" class="dock-menu__back" (click)="attachMenuView = 'main'">
                      <i class="pi pi-chevron-left"></i>
                      <span>{{ 'chatbot.attachMenu.selectExpert' | transloco }}</span>
                    </button>
                    <div class="dock-menu__scroll">
                      <button *ngFor="let skill of chatSkills; trackBy: trackByIndex"
                              type="button"
                              class="dock-menu__item"
                              (click)="selectSkill(skill)">
                        <i class="pi dock-menu__icon" [ngClass]="'pi-' + skill.icon"></i>
                        <span class="dock-menu__name">{{ skill.name }}</span>
                      </button>
                    </div>
                    <div class="dock-menu__sep"></div>
                    <button type="button" class="dock-menu__item dock-menu__item--quiet"
                            (click)="openSkillsManager()">
                      <i class="pi pi-cog dock-menu__icon"></i>
                      <span class="dock-menu__name">{{ 'chatbot.skillsManager.manage' | transloco }}</span>
                    </button>
                  </ng-container>

                </div>
                <button class="attach-btn"
                        (click)="toggleAttachMenu()"
                        [disabled]="isStreaming"
                        aria-label="Anexar arquivo">
                  <i class="pi pi-plus"></i>
                </button>
              </div>
              <!-- Seletor de perfil (Eco/Sport/Race). Era um <select> nativo: a lista abria
                   com o cromo do sistema operacional (aquele azul do Windows) e a descricao
                   de cada opcao so existia como title. Agora usa o mesmo popover do menu de
                   anexo e mostra a descricao na tela. Perfil bloqueado (available=false)
                   fica esmaecido mas continua clicavel: leva direto pro CTA (upsell de
                   plano ou recarga de tanques). -->
              <div class="model-picker" *ngIf="profileSelectorVisible">
                <div *ngIf="profileMenuOpen" class="dock-menu__backdrop" (click)="profileMenuOpen = false"></div>
                <div *ngIf="profileMenuOpen"
                     class="dock-menu dock-menu--right"
                     role="listbox"
                     [attr.aria-label]="'chatbot.profileSelectorLabel' | transloco">
                  <span class="dock-menu__label">{{ 'chatbot.profileSelectorTitle' | transloco }}</span>
                  <div class="dock-menu__scroll">
                  <button *ngFor="let p of profileOptions; trackBy: trackByProfileKey"
                          type="button"
                          role="option"
                          class="dock-menu__item dock-menu__item--stacked"
                          [class.dock-menu__item--quiet]="!p.available"
                          [class.dock-menu__item--current]="p.available && p.key === displayedProfileKey"
                          [attr.aria-selected]="p.key === displayedProfileKey"
                          [attr.aria-disabled]="!p.available"
                          (click)="pickProfile(p)">
                    <span class="dock-menu__row">
                      <span class="dock-menu__name">{{ profileDisplayName(p) }}</span>
                      <i class="pi pi-check dock-menu__check" *ngIf="p.available"></i>
                      <i class="pi pi-lock profile-lock-icon" *ngIf="!p.available"></i>
                    </span>
                    <span class="dock-menu__desc" *ngIf="profileDescription(p)">{{ profileDescription(p) }}</span>
                    <span class="dock-menu__desc profile-locked-cta" *ngIf="!p.available">
                      {{ profileLockedKey(p) | transloco: { time: freeTankRefillTime } }}
                    </span>
                  </button>
                  </div>
                </div>
                <button type="button"
                        class="model-trigger"
                        [class.model-trigger--open]="profileMenuOpen"
                        [disabled]="isStreaming"
                        (click)="toggleProfileMenu()"
                        [attr.aria-label]="'chatbot.profileSelectorLabel' | transloco"
                        [attr.aria-expanded]="profileMenuOpen">
                  <span class="model-trigger__name">{{ currentProfileName }}</span>
                  <i class="pi pi-chevron-down model-trigger__chevron"></i>
                </button>
              </div>
              <button pButton
                      class="send-btn"
                      [icon]="rateLimitCountdown > 0 ? '' : 'pi pi-arrow-up'"
                      [label]="rateLimitCountdown > 0 ? ('chatbot.rateLimitCountdown' | transloco: { seconds: rateLimitCountdown }) : ''"
                      aria-label="Enviar mensagem"
                      (click)="sendMessage()"
                      [disabled]="isStreaming || rateLimitCountdown > 0 || !canSend">
              </button>
            </div>
          </div>
        </div>
      </div>

      <p class="ai-warning" *ngIf="!profilesLoading && !profilesEnabled">{{ 'chatbot.profilesUnavailable' | transloco }}</p>
      <p class="ai-warning" *ngIf="profilesLoading || profilesEnabled">{{ 'chatbot.aiWarning' | transloco }}</p>
    </div>

  </div>

<p-confirmDialog [style]="{ width: '92vw', 'max-width': '440px' }"></p-confirmDialog>

<!-- Skills Manager Dialog -->
<p-dialog
  [(visible)]="skillsManagerVisible"
  [header]="(skillsManagerMode === 'form' ? (skillFormMode === 'create' ? 'chatbot.skillsManager.newSkill' : 'chatbot.skillsManager.editSkill') : 'chatbot.skillsManager.title') | transloco"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '90vw', 'max-width': '480px' }"
  styleClass="skills-mgr-dialog"
>

  <!-- ── List mode ── -->
  <ng-container *ngIf="skillsManagerMode === 'list'">
    <div *ngIf="customSkillsLoading" class="sm-loading">
      <p-progressSpinner styleClass="small-spinner" />
    </div>

    <div *ngIf="!customSkillsLoading && !customSkills.length" class="sm-empty">
      <i class="pi pi-bolt"></i>
      <span>{{ 'chatbot.skillsManager.empty' | transloco }}</span>
    </div>

    <div *ngIf="!customSkillsLoading && customSkills.length" class="sm-list">
      <div *ngFor="let skill of customSkills; trackBy: trackByIndex" class="sm-skill-row">
        <span class="sm-skill-icon"><i class="pi" [ngClass]="'pi-' + skill.icon"></i></span>
        <span class="sm-skill-name">{{ skill.name }}</span>
        <div class="sm-skill-actions">
          <button class="sm-btn sm-btn-icon" (click)="openEditSkill(skill)" [title]="'common.buttons.edit' | transloco">
            <i class="pi pi-pencil"></i>
          </button>
          <button class="sm-btn sm-btn-icon sm-btn-danger" (click)="confirmDeleteCustomSkill(skill)" [title]="'common.buttons.delete' | transloco">
            <i class="pi pi-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ── Form mode ── -->
  <ng-container *ngIf="skillsManagerMode === 'form'">
    <div class="sm-form">
      <div class="sm-form-group">
        <label class="sm-label">{{ 'chatbot.skillsManager.fieldName' | transloco }} <span class="sm-required">*</span></label>
        <input pInputText [(ngModel)]="skillForm.name" maxlength="80" [placeholder]="'chatbot.skillsManager.namePlaceholder' | transloco" class="sm-input-full" />
      </div>

      <div class="sm-form-row">
        <div class="sm-form-group sm-form-half">
          <label class="sm-label">{{ 'chatbot.skillsManager.fieldIcon' | transloco }}</label>
          <p-dropdown
            [options]="skillIconOptions"
            [(ngModel)]="skillForm.icon"
            optionLabel="label"
            optionValue="value"
            [style]="{ width: '100%' }"
          >
            <ng-template pTemplate="selectedItem" let-item>
              <span class="sm-icon-opt"><i class="pi" [ngClass]="'pi-' + item.value"></i> {{ item.label }}</span>
            </ng-template>
            <ng-template pTemplate="item" let-item>
              <span class="sm-icon-opt"><i class="pi" [ngClass]="'pi-' + item.value"></i> {{ item.label }}</span>
            </ng-template>
          </p-dropdown>
        </div>
        <div class="sm-form-group sm-form-half">
          <label class="sm-label">{{ 'chatbot.skillsManager.fieldActive' | transloco }}</label>
          <div class="sm-switch-row">
            <p-inputSwitch [(ngModel)]="skillForm.isActive"></p-inputSwitch>
            <span class="sm-switch-label">{{ (skillForm.isActive ? 'chatbot.skillsManager.active' : 'chatbot.skillsManager.inactive') | transloco }}</span>
          </div>
        </div>
      </div>

      <div class="sm-form-group">
        <label class="sm-label">{{ 'chatbot.skillsManager.fieldPrompt' | transloco }} <span class="sm-required">*</span></label>
        <textarea
          pInputTextarea
          [(ngModel)]="skillForm.prompt"
          maxlength="4000"
          [placeholder]="'chatbot.skillsManager.promptPlaceholder' | transloco"
          [rows]="5"
          [autoResize]="false"
          class="sm-input-full sm-textarea"
        ></textarea>
      </div>
    </div>
  </ng-container>

  <ng-template pTemplate="footer">
    <!-- List footer -->
    <ng-container *ngIf="skillsManagerMode === 'list'">
      <button class="sm-btn sm-btn-outline" (click)="skillsManagerVisible = false">
        {{ 'common.buttons.cancel' | transloco }}
      </button>
      <button class="sm-btn sm-btn-primary" (click)="openCreateSkill()">
        <i class="pi pi-plus"></i>
        {{ 'chatbot.skillsManager.newSkill' | transloco }}
      </button>
    </ng-container>
    <!-- Form footer -->
    <ng-container *ngIf="skillsManagerMode === 'form'">
      <button class="sm-btn sm-btn-outline" (click)="skillsManagerMode = 'list'">
        <i class="pi pi-arrow-left"></i>
        {{ 'common.buttons.back' | transloco }}
      </button>
      <button class="sm-btn sm-btn-primary" (click)="saveSkill()" [disabled]="skillFormSaving || !skillForm.name.trim() || !skillForm.prompt.trim()">
        <i class="pi pi-spin pi-spinner" *ngIf="skillFormSaving"></i>
        <i class="pi pi-check" *ngIf="!skillFormSaving"></i>
        {{ 'common.buttons.save' | transloco }}
      </button>
    </ng-container>
  </ng-template>
</p-dialog>

<!-- Upload dialog -->
<p-dialog
  [header]="'chatbot.uploadDialog.header' | transloco"
  [(visible)]="uploadDialogVisible"
  [modal]="true"
  [draggable]="false"
  [style]="{ width: '90vw', 'max-width': '520px' }"
>
  <!-- Tabs -->
  <div class="ud-tabs">
    <button class="ud-tab" [class.active]="uploadDialogTab === 'browse'" (click)="uploadDialogTab = 'browse'; currentBrowsePath = ''">
      <i class="pi pi-folder-open"></i> {{ 'chatbot.uploadDialog.tabBrowse' | transloco }}
    </button>
    <button class="ud-tab" [class.active]="uploadDialogTab === 'upload'" (click)="uploadDialogTab = 'upload'">
      <i class="pi pi-upload"></i> {{ 'chatbot.uploadDialog.tabUpload' | transloco }}
    </button>
  </div>

  <!-- ── Browse tab ── -->
  <div *ngIf="uploadDialogTab === 'browse'" class="browse-panel">

    <div *ngIf="existingFilesLoading" class="browse-loading">
      <i class="pi pi-spin pi-spinner"></i>
    </div>

    <div *ngIf="!existingFilesLoading && !existingFiles.length" class="browse-empty">
      {{ 'chatbot.uploadDialog.browseEmpty' | transloco }}
    </div>

    <ng-container *ngIf="!existingFilesLoading && existingFiles.length">
      <!-- Breadcrumb / back -->
      <div *ngIf="currentBrowsePath" class="browse-breadcrumb">
        <button class="browse-back-btn" (click)="navigateBrowseUp()" aria-label="Voltar">
          <i class="pi pi-arrow-left"></i>
        </button>
        <button class="browse-root-btn" (click)="navigateBrowseRoot()">{{ 'chatbot.uploadDialog.browseRoot' | transloco }}</button>
        <ng-container *ngFor="let seg of getBrowseBreadcrumb(); let i = index; trackBy: trackByIndex">
          <span class="browse-breadcrumb-sep">/</span>
          <span class="browse-breadcrumb-seg">{{ seg }}</span>
        </ng-container>
      </div>

      <!-- Folders -->
      <div *ngFor="let folder of getBrowseFolders(); trackBy: trackByIndex"
           class="browse-item browse-folder"
           (click)="navigateBrowse(folder.path)">
        <i class="pi pi-folder browse-item-icon"></i>
        <span class="browse-item-name">{{ folder.name }}</span>
        <i class="pi pi-chevron-right browse-item-chevron"></i>
      </div>

      <!-- Files -->
      <div *ngFor="let file of getBrowseFiles(); trackBy: trackByIndex"
           class="browse-item browse-file"
           (click)="selectExistingFile(file)">
        <i class="pi browse-item-icon"
           [class.pi-map]="(file.type || '') === 'map'"
           [class.pi-file]="(file.type || '') !== 'map'"></i>
        <span class="browse-item-name">{{ file.fileName }}</span>
        <span class="browse-item-badge">{{ (file.type || '') === 'map' ? ('chatbot.atFile.badgeMap' | transloco) : ('chatbot.atFile.badgeLog' | transloco) }}</span>
      </div>

      <div *ngIf="!getBrowseFolders().length && !getBrowseFiles().length" class="browse-empty">
        {{ 'chatbot.uploadDialog.folderEmpty' | transloco }}
      </div>
    </ng-container>
  </div>

  <!-- ── Upload tab ── -->
  <div *ngIf="uploadDialogTab === 'upload'" class="upload-dialog-body">

    <!-- File picker -->
    <div class="upload-section">
      <label class="upload-label">{{ 'chatbot.uploadDialog.fileLabel' | transloco }}</label>
      <input #chatUploadInput type="file" multiple accept=".ftlog,.ftml,.ftmap,.ftm" hidden (change)="onChatUploadFileSelected($event)" />

      <div *ngIf="!chatUploadFiles.length"
           class="upload-dropzone"
           (click)="chatUploadInput.click()">
        <i class="pi pi-cloud-upload upload-icon"></i>
        <span class="upload-placeholder">{{ 'chatbot.uploadDialog.dropHint' | transloco }}</span>
      </div>

      <div *ngIf="chatUploadFiles.length" class="upload-files-list">
        <div *ngFor="let f of chatUploadFiles; let i = index; trackBy: trackByIndex" class="upload-file-row">
          <i class="pi upload-file-row-icon"
             [class.pi-map]="detectUploadFileType(f.name) === 'map'"
             [class.pi-file]="detectUploadFileType(f.name) === 'log'"></i>
          <div class="upload-file-row-info">
            <span class="upload-file-name">{{ f.name }}</span>
            <span class="upload-file-size">
              {{ formatUploadBytes(f.size) }}
              · {{ detectUploadFileType(f.name) === 'map' ? ('chatbot.atFile.badgeMap' | transloco) : ('chatbot.atFile.badgeLog' | transloco) }}
            </span>
          </div>
          <button type="button" class="upload-file-row-remove" (click)="removeChatUploadFile(i)" aria-label="Remover arquivo">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <button type="button" class="upload-add-more" (click)="chatUploadInput.click()">
          <i class="pi pi-plus"></i> {{ 'chatbot.uploadDialog.addMore' | transloco }}
        </button>
      </div>
    </div>

  </div>

  <ng-template pTemplate="footer">
    <p-button
      [label]="'chatbot.uploadDialog.cancel' | transloco"
      [text]="true"
      (onClick)="uploadDialogVisible = false"
    />
    <p-button
      *ngIf="uploadDialogTab === 'upload'"
      [label]="'chatbot.uploadDialog.send' | transloco"
      icon="pi pi-upload"
      severity="danger"
      [disabled]="!canStartChatUpload"
      (onClick)="startChatUpload()"
    />
  </ng-template>
</p-dialog>

<!-- Marca do turbo: a presença do assistente. Ociosa na tela vazia, girando
     enquanto ele trabalha. Um objeto, dois estados — por isso vive num
     template único em vez de duplicado nos dois lugares. -->
<ng-template #turboMark>
  <svg class="turbo-svg" width="38" height="34" viewBox="0 0 96 84" xmlns="http://www.w3.org/2000/svg">
    <path d="M 92.773 9.859 C 92.773 9.859 85.252 9.712 73.971 9.523 C 72.076 9.491 67.1 9.458 65.138 9.424 C 53.63 9.226 42.217 6.722 36.206 7.297 C 27.265 8.152 21.882 7.483 12.383 16.699 C 9.114 19.871 8.942 20.122 6.896 23.439 C 5.115 26.326 3.241 32.27 2.366 35.169 C 1.427 38.282 1.842 41.254 2.26 44.464 C 2.96 49.834 4.659 55.375 7.288 59.731 C 12.007 67.549 18.595 72.725 32.262 75.927 C 41.621 78.12 51.641 74.603 59.151 69.078 C 69.445 61.504 75.266 48.947 70.42 31.089" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <line x1="92.826" y1="10.077" x2="92.826" y2="29.324" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <line x1="71.101" y1="30.149" x2="93.101" y2="30.149" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <circle cx="38" cy="42" r="22" fill="none" stroke="currentColor" stroke-width="1.5"/>
    <line x1="0" y1="33" x2="8" y2="33" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <line x1="0" y1="51" x2="8" y2="51" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
    <circle cx="5" cy="42" r="2.5" fill="currentColor"/>
    <circle cx="70.186" cy="44.134" r="2.5" fill="currentColor"/>
  <g class="turbo-wheel">
    <circle cx="38" cy="42" r="17" class="turbo-hub"/>
    <circle cx="38" cy="42" r="17" fill="none" stroke="currentColor" stroke-width="2"/>
    <circle cx="38" cy="42" r="5" fill="none" stroke="currentColor" stroke-width="2"/>
    <line x1="43" y1="42" x2="55" y2="42" stroke="currentColor" stroke-width="2"/>
    <line x1="40.5" y1="46.33" x2="46.5" y2="56.72" stroke="currentColor" stroke-width="2"/>
    <line x1="35.5" y1="46.33" x2="29.5" y2="56.72" stroke="currentColor" stroke-width="2"/>
    <line x1="33" y1="42" x2="21" y2="42" stroke="currentColor" stroke-width="2"/>
    <line x1="35.5" y1="37.67" x2="29.5" y2="27.28" stroke="currentColor" stroke-width="2"/>
    <line x1="40.5" y1="37.67" x2="46.5" y2="27.28" stroke="currentColor" stroke-width="2"/>
    <circle cx="38" cy="42" r="2.5" fill="currentColor"/>
  </g>
  </svg>
</ng-template>
